import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Download, Pencil, Search, UserRound } from "lucide-react";
import { PageHeader } from "../../components/domain/Bits";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { api, toastError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { canAtLeast, useEffectiveRole } from "../../lib/roles";
import User360Panel from "./User360Panel";
import { toast } from "sonner";

type Section = "contacts" | "visits" | "interactions" | "conversions" | "coaching" | "employee_relations" | "equipment" | "pipeline" | "invoices";
interface TenantUser { id: string; name: string; email: string; active: boolean }
interface Overview {
  user: { id: string; name: string | null; email: string | null; tenant_role: string | null; active: boolean };
  performance_summary: Record<string, number>;
  commission_summary: { total: number; unpaid: number; paid: number; non_commissionable: number };
}
interface RecordsPage { items: Array<Record<string, unknown>>; page: number; limit: number; total: number; stage_counts?: Record<string, number> }

type EvidenceSection = "employment" | "training" | "coaching" | "employee_relations" | "requests" | "equipment" | "commissions" | "invoices" | "visits" | "audit";

const EVIDENCE_SECTIONS: Array<{ id: EvidenceSection; label: string }> = [
  { id: "employment", label: "Employment information" },
  { id: "training", label: "Training & policy acknowledgments" },
  { id: "coaching", label: "Coaching & incident records" },
  { id: "employee_relations", label: "Employee relations outcomes" },
  { id: "requests", label: "Staff requests" },
  { id: "equipment", label: "Equipment & offboarding clearance" },
  { id: "commissions", label: "Commissions" },
  { id: "invoices", label: "Invoices" },
  { id: "visits", label: "Visits" },
  { id: "audit", label: "Audit history" },
];

const SECTIONS: Array<{ id: Section; label: string }> = [
  { id: "contacts", label: "Assigned doctors & contacts" },
  { id: "visits", label: "Visits" },
  { id: "interactions", label: "Activity" },
  { id: "conversions", label: "Conversions & earnings" },
  { id: "coaching", label: "Coaching" },
  { id: "employee_relations", label: "Employee Relations" },
  { id: "equipment", label: "Equipment" },
  { id: "pipeline", label: "Current Pipeline" },
  { id: "invoices", label: "Invoices" },
];

const display = (value: unknown) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}T/.test(text)) return new Date(text).toLocaleString();
  return text.replace(/_/g, " ");
};

export default function User360Workspace({ selfOnly = false }: { selfOnly?: boolean }) {
  const { user } = useAuth();
  const role = useEffectiveRole();
  const { userId: routeUserId } = useParams();
  const navigate = useNavigate();
  const canChooseUser = !selfOnly && (role === "owner_admin" || role === "manager_trainer");
  const [users, setUsers] = useState<TenantUser[]>([]);
  const selectedUserId = selfOnly ? user?.id : (routeUserId || user?.id);
  const [overview, setOverview] = useState<Overview | null>(null);
  const [section, setSection] = useState<Section>("contacts");
  const [records, setRecords] = useState<RecordsPage>({ items: [], page: 1, limit: 20, total: 0 });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState<"pdf" | "xlsx" | "csv">("pdf");
  const [exportSections, setExportSections] = useState<EvidenceSection[]>(EVIDENCE_SECTIONS.map((item) => item.id));
  const [includePrivateNotes, setIncludePrivateNotes] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: "", email: "", role: "representative" });
  const canManageUser = !selfOnly && role === "owner_admin";

  useEffect(() => {
    if (!canChooseUser) return;
    api.get<TenantUser[]>("/tenant/user-360-directory").then(({ data }) => {
      setUsers(data || []);
      if (!routeUserId && data?.[0]?.id) navigate(`/user-360/${data[0].id}`, { replace: true });
    }).catch(toastError);
  }, [canChooseUser, navigate, routeUserId]);

  useEffect(() => {
    if (!selectedUserId) return;
    setOverview(null);
    api.get<Overview>(`/tenant/users/${selectedUserId}/360`)
      .then(({ data }) => setOverview(data))
      .catch(toastError);
  }, [selectedUserId]);

  const loadRecords = useCallback(async (page = 1) => {
    if (!selectedUserId) return;
    setLoading(true);
    try {
      const { data } = await api.get<RecordsPage>(`/tenant/users/${selectedUserId}/360/records`, {
        params: { section, search: search.trim(), status, from_date: fromDate, to_date: toDate, page, limit: 20 },
      });
      setRecords(data);
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  }, [selectedUserId, section, search, status, fromDate, toDate]);

  useEffect(() => { loadRecords(1); }, [loadRecords]);

  const beginEdit = () => {
    if (!overview) return;
    setProfileForm({
      name: overview.user.name || "",
      email: overview.user.email || "",
      role: overview.user.tenant_role || "viewer",
    });
    setEditOpen(true);
  };

  const saveProfile = async () => {
    if (!selectedUserId || !profileForm.name.trim() || !profileForm.email.trim()) return;
    setSavingProfile(true);
    try {
      await api.patch(`/tenant/users/${selectedUserId}/profile`, {
        name: profileForm.name.trim(),
        email: profileForm.email.trim(),
        role: profileForm.role,
      });
      setOverview((current) => current ? {
        ...current,
        user: { ...current.user, name: profileForm.name.trim(), email: profileForm.email.trim(), tenant_role: profileForm.role },
      } : current);
      setUsers((current) => current.map((item) => item.id === selectedUserId
        ? { ...item, name: profileForm.name.trim(), email: profileForm.email.trim() }
        : item));
      setEditOpen(false);
      toast.success("User profile updated");
    } catch (error) {
      toastError(error);
    } finally {
      setSavingProfile(false);
    }
  };

  const exportCsv = async () => {
    if (!selectedUserId) return;
    try {
      const response = await api.get(`/tenant/users/${selectedUserId}/360/export`, {
        params: { section, search: search.trim(), status, from_date: fromDate, to_date: toDate },
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `user-360-${section}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) { toastError(error); }
  };

  const exportEvidencePackage = async () => {
    if (!selectedUserId || exportSections.length === 0) return;
    setExporting(true);
    try {
      const response = await api.get(`/tenant/users/${selectedUserId}/360/evidence-export`, {
        params: {
          format: exportFormat,
          sections: exportSections.join(","),
          from_date: fromDate,
          to_date: toDate,
          include_private_notes: role === "owner_admin" && includePrivateNotes,
        },
        responseType: "blob",
      });
      const disposition = String(response.headers["content-disposition"] || "");
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const filename = match?.[1] || `user-360-evidence.${exportFormat}`;
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setExportOpen(false);
      toast.success("Formal evidence package downloaded");
    } catch (error) {
      toastError(error);
    } finally {
      setExporting(false);
    }
  };

  const toggleEvidenceSection = (value: EvidenceSection) => {
    setExportSections((current) => current.includes(value)
      ? current.filter((item) => item !== value)
      : [...current, value]);
  };

  const columns = useMemo(() => {
    const preferred: Record<Section, string[]> = {
      contacts: ["name", "status", "location", "email", "phone", "updated_at"],
      visits: ["title", "contact_name", "status", "when", "outcome", "verified_at"],
      interactions: ["kind", "contact_name", "outcome", "occurred_at", "summary"],
      conversions: ["patient_name", "status", "converted_at", "amount", "currency", "commissionable"],
      coaching: ["status", "observation", "feedback", "action", "due_date", "updated_at"],
      employee_relations: ["case_number", "status", "issue_type", "finding", "outcome", "root_cause", "resolved_at", "linked_coaching_id"],
      equipment: ["equipment_type", "description", "asset_number", "quantity", "status", "released_at", "return_due_at", "staff_receipt_signature", "returned_at", "management_return_acknowledged_at"],
      pipeline: ["name", "contact_name", "organization_name", "stage", "status", "value", "currency", "expected_close_date", "updated_at"],
      invoices: ["invoice_number", "status", "invoice_date", "due_date", "subtotal", "tax_amount", "total_amount", "currency", "submitted_at", "paid_at", "payment_reference"],
    };
    return preferred[section];
  }, [section]);

  if (!user || !selectedUserId) return null;
  const summary = overview?.performance_summary || {};
  const lastPage = Math.max(1, Math.ceil(records.total / records.limit));

  return (
    <>
      <PageHeader
        title={selfOnly ? "My Performance" : "User 360"}
        subtitle="Assignments, activity, coaching, and finalized employee-relations outcomes in one accountable view"
        actions={<div className="flex gap-2">
          {canManageUser && <Button variant="outline" onClick={beginEdit} data-testid="edit-user-360"><Pencil className="mr-2 h-4 w-4" /> Edit user</Button>}
          <Button variant="outline" onClick={exportCsv}><Download className="mr-2 h-4 w-4" /> Export current view</Button>
          {canChooseUser && <Button onClick={() => setExportOpen(true)} data-testid="open-evidence-export"><Download className="mr-2 h-4 w-4" /> Evidence package</Button>}
        </div>}
      />
      <div className="mx-auto max-w-7xl space-y-5 p-6 md:p-8" data-testid="user-360-workspace">
        <section className="rounded-lg border border-slate-200 bg-white p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="grid h-11 w-11 place-items-center rounded-full bg-slate-100"><UserRound className="h-5 w-5" /></div>
              <div>
                <h2 className="font-semibold">{overview?.user.name || "Loading user…"}</h2>
                <p className="text-sm text-slate-500">{overview?.user.email} · {display(overview?.user.tenant_role)}</p>
              </div>
            </div>
            {canChooseUser && (
              <Select value={selectedUserId} onValueChange={(id) => navigate(`/user-360/${id}`)}>
                <SelectTrigger className="w-72" data-testid="user-360-user-select"><SelectValue placeholder="Choose user" /></SelectTrigger>
                <SelectContent>{users.map((item) => <SelectItem key={item.id} value={item.id}>{item.name} · {item.email}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
          <div className="mt-5 grid gap-3 sm:grid-cols-3 lg:grid-cols-7">
            {[
              ["Assigned", summary.assigned_contacts || 0],
              ["Visits", summary.planned_visits || 0],
              ["Completed", summary.completed_visits || 0],
              ["Interactions", summary.interactions || 0],
              ["Conversions", summary.conversions || 0],
              ["Open Pipeline", summary.open_pipeline || 0],
              ["Open coaching", summary.open_coaching || 0],
            ].map(([label, value]) => <div key={label} className="rounded-md bg-slate-50 p-3"><div className="text-xl font-semibold">{value}</div><div className="text-xs text-slate-500">{label}</div></div>)}
          </div>
        </section>

        {canManageUser && selectedUserId && <User360Panel userId={selectedUserId} managementOnly />}

        <div className="flex gap-2 overflow-x-auto pb-1">
          {SECTIONS.map((item) => <Button key={item.id} variant={section === item.id ? "default" : "outline"} onClick={() => { setSection(item.id); setStatus(""); }} className="whitespace-nowrap">{item.label}</Button>)}
        </div>

        <section className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <div className="grid gap-3 border-b border-slate-200 p-4 md:grid-cols-4">
            <div className="relative md:col-span-2"><Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" /><Input className="pl-9" placeholder="Search this view" value={search} onChange={(e) => setSearch(e.target.value)} /></div>
            <Input placeholder="Status or type" value={status} onChange={(e) => setStatus(e.target.value)} />
            <div className="flex gap-2"><Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} /><Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} /></div>
          </div>
          {section === "pipeline" && records.stage_counts && Object.keys(records.stage_counts).length > 0 && (
            <div className="flex flex-wrap gap-2 border-b border-slate-200 px-4 py-3" data-testid="pipeline-stage-counts">
              {Object.entries(records.stage_counts).map(([label, count]) => (
                <span key={label} className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700">
                  {label}: {count}
                </span>
              ))}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid={`user-360-${section}-table`}>
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500"><tr>{columns.map((column) => <th key={column} className="px-4 py-3 text-left font-medium">{column.replace(/_/g, " ")}</th>)}</tr></thead>
              <tbody>
                {!loading && records.items.map((row, index) => <tr key={String(row.id || index)} className="border-t border-slate-100">{columns.map((column) => <td key={column} className="max-w-xs px-4 py-3 align-top text-slate-700">{((section === "pipeline" && column === "name") || (section === "invoices" && column === "invoice_number")) && row.detail_path ? <Link className="font-medium text-blue-700 hover:underline" to={String(row.detail_path)}>{display(row[column])}</Link> : display(row[column])}</td>)}</tr>)}
              </tbody>
            </table>
            {loading && <div className="p-8 text-center text-sm text-slate-500">Loading records…</div>}
            {!loading && records.items.length === 0 && <div className="p-8 text-center text-sm text-slate-500">No matching records.</div>}
          </div>
          <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3 text-sm">
            <span>{records.total} record{records.total === 1 ? "" : "s"}</span>
            <div className="flex items-center gap-2"><Button variant="outline" size="sm" disabled={records.page <= 1} onClick={() => loadRecords(records.page - 1)}>Previous</Button><span>Page {records.page} of {lastPage}</span><Button variant="outline" size="sm" disabled={records.page >= lastPage} onClick={() => loadRecords(records.page + 1)}>Next</Button></div>
          </div>
        </section>
      </div>

      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="max-w-2xl" data-testid="user-360-evidence-dialog">
          <DialogHeader>
            <DialogTitle>Formal staff evidence package</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">
            Create a company-branded, date-filtered staff profile for formal records. Select exactly what should be included.
          </p>
          <div className="grid gap-4">
            <div>
              <Label>File format</Label>
              <Select value={exportFormat} onValueChange={(value: "pdf" | "xlsx" | "csv") => setExportFormat(value)}>
                <SelectTrigger data-testid="evidence-export-format"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pdf">Formal PDF</SelectItem>
                  <SelectItem value="xlsx">Excel workbook</SelectItem>
                  <SelectItem value="csv">CSV evidence package</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="mb-2 flex items-center justify-between">
                <Label>Sections</Label>
                <button type="button" className="text-xs text-blue-700 hover:underline" onClick={() => setExportSections(
                  exportSections.length === EVIDENCE_SECTIONS.length ? [] : EVIDENCE_SECTIONS.map((item) => item.id)
                )}>{exportSections.length === EVIDENCE_SECTIONS.length ? "Clear all" : "Select all"}</button>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                {EVIDENCE_SECTIONS.map((item) => (
                  <label key={item.id} className="flex cursor-pointer items-center gap-2 rounded-md border border-slate-200 p-3 text-sm">
                    <input type="checkbox" checked={exportSections.includes(item.id)} onChange={() => toggleEvidenceSection(item.id)} />
                    {item.label}
                  </label>
                ))}
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div><Label htmlFor="evidence-from">From date</Label><Input id="evidence-from" type="date" value={fromDate} onChange={(event) => setFromDate(event.target.value)} /></div>
              <div><Label htmlFor="evidence-to">To date</Label><Input id="evidence-to" type="date" value={toDate} onChange={(event) => setToDate(event.target.value)} /></div>
            </div>
            {role === "owner_admin" && (
              <label className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
                <input className="mt-0.5" type="checkbox" checked={includePrivateNotes} onChange={(event) => setIncludePrivateNotes(event.target.checked)} data-testid="include-private-notes" />
                <span><strong>Include private management notes</strong><span className="mt-1 block text-xs text-slate-600">Off by default. Turn this on only when the formal record specifically requires private management evidence.</span></span>
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExportOpen(false)}>Cancel</Button>
            <Button onClick={exportEvidencePackage} disabled={exporting || exportSections.length === 0} data-testid="download-evidence-package">
              {exporting ? "Preparing…" : "Download package"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="edit-user-360-dialog">
          <DialogHeader><DialogTitle>Edit {overview?.user.name || "user"}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div><Label htmlFor="user-360-name">Name</Label><Input id="user-360-name" value={profileForm.name} onChange={(event) => setProfileForm({ ...profileForm, name: event.target.value })} /></div>
            <div><Label htmlFor="user-360-email">Email</Label><Input id="user-360-email" type="email" value={profileForm.email} onChange={(event) => setProfileForm({ ...profileForm, email: event.target.value })} /></div>
            <div><Label>Role</Label>
              <Select value={profileForm.role} onValueChange={(value) => setProfileForm({ ...profileForm, role: value })}>
                <SelectTrigger data-testid="user-360-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["owner_admin", "manager_trainer", "representative", "viewer"].map((item) => <SelectItem key={item} value={item}>{display(item)}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="mt-1 text-xs text-slate-500">Role changes take effect immediately. The last active Owner cannot be demoted.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={saveProfile} disabled={savingProfile || !profileForm.name.trim() || !profileForm.email.trim()}>{savingProfile ? "Saving…" : "Save changes"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
